import React, { useState, useMemo } from 'react';
import { Card, Table, Button, Space, Typography, DatePicker, Input, Select, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../utils/api';
import type { AuditLog } from '../types';
import type { ColumnsType } from 'antd/es/table';
import AppLayout from '../components/layout/AppLayout';
import BreadcrumbNav from '../components/layout/BreadcrumbNav';
import dayjs, { Dayjs } from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogsPage: React.FC = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [actionFilter, setActionFilter] = useState<string | undefined>(undefined);

  const { data: auditLogsData = [], isLoading, refetch } = useQuery<AuditLog[]>({
    queryKey: ['auditLogs'],
    queryFn: () => apiClient.getAuditLogs() as Promise<AuditLog[]>,
  });

  const filteredLogs = useMemo(() => {
    let result = auditLogsData;

    // Filter by search text
    if (searchText) {
      result = result.filter(log =>
        log.user.toLowerCase().includes(searchText.toLowerCase()) ||
        log.action.toLowerCase().includes(searchText.toLowerCase()) ||
        log.details.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      result = result.filter(log => {
        const logDate = dayjs(log.timestamp);
        return logDate.isAfter(dateRange[0]!) && logDate.isBefore(dateRange[1]!);
      });
    }

    // Filter by action type
    if (actionFilter) {
      result = result.filter(log => log.action === actionFilter);
    }

    return result;
  }, [auditLogsData, searchText, dateRange, actionFilter]);

  const getActionColor = (action: string) => {
    const colorMap: { [key: string]: string } = {
      'CREATE': 'green',
      'UPDATE': 'blue',
      'DELETE': 'red',
      'LOGIN': 'purple',
      'LOGOUT': 'orange',
      'CONNECT': 'green',
      'DISCONNECT': 'red',
    };
    return colorMap[action] || 'default';
  };

  const auditLogColumns: ColumnsType<AuditLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 120,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search user"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.user.toLowerCase().includes(value.toString().toLowerCase()),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action: string) => (
        <Tag color={getActionColor(action)}>
          {action}
        </Tag>
      ),
      filters: [
        { text: 'CREATE', value: 'CREATE' },
        { text: 'UPDATE', value: 'UPDATE' },
        { text: 'DELETE', value: 'DELETE' },
        { text: 'LOGIN', value: 'LOGIN' },
        { text: 'LOGOUT', value: 'LOGOUT' },
        { text: 'CONNECT', value: 'CONNECT' },
        { text: 'DISCONNECT', value: 'DISCONNECT' },
      ],
      onFilter: (value, record) => record.action === value,
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
    },
  ];

  return (
    <AppLayout
      title="Audit Logs"
      extraActions={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          Refresh
        </Button>
      }
    >
      <BreadcrumbNav />

      <Card
        title={
          <span>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Audit Logs
          </span>
        }
      >
        <div className="responsive-filters" style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search logs..."
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => !e.target.value && setSearchText('')}
            style={{ width: 250 }}
          />
          <RangePicker
            placeholder={['Start date', 'End date']}
            onChange={(dates: [Dayjs | null, Dayjs | null] | null) => setDateRange(dates)}
            style={{ width: 250 }}
          />
          <Select
            placeholder="Filter by action"
            allowClear
            value={actionFilter}
            onChange={value => setActionFilter(value)}
            style={{ width: 150 }}
          >
            <Option value="CREATE">CREATE</Option>
            <Option value="UPDATE">UPDATE</Option>
            <Option value="DELETE">DELETE</Option>
            <Option value="LOGIN">LOGIN</Option>
            <Option value="LOGOUT">LOGOUT</Option>
            <Option value="CONNECT">CONNECT</Option>
            <Option value="DISCONNECT">DISCONNECT</Option>
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
        </div>
        <Table
          columns={auditLogColumns}
          dataSource={filteredLogs}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </AppLayout>
  );
};

export default AuditLogsPage;
